import React, { useEffect,useState,useRef,useMemo,useCallback} from "react";
import dayjs, { Dayjs } from "dayjs";
import "dayjs/locale/ja";
import {Box,IconButton,InputAdornment,Popover,TextField, Typography} from "@mui/material";
import EventIcon from "@mui/icons-material/Event";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import {LocalizationProvider,} from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs,} from "@mui/x-date-pickers/AdapterDayjs";
import { CalendarPicker,} from "@mui/x-date-pickers/CalendarPicker";
import {PickersDay,PickersDayProps,} from "@mui/x-date-pickers/PickersDay";
import CloseIcon from "@mui/icons-material/Close";
import { useTranslation } from "react-i18next";
import CONSTANTS from "../../../constants/constants";

const DEFAULT_MAX_RANGE_DAYS = CONSTANTS.NUNBER_OF_DAYS;
const calenderStartDate = dayjs(CONSTANTS.CALENDAR_START_DATE).startOf("day");

interface CustomRangePickerProps {
    valueFrom?: Date | null;
    valueTo?: Date | null;
    onDateRangeChange?: (from: Date | null, to: Date | null) => void;
    showSnackbar?: (message: string, success?: boolean) => void;
    onDateFromChange?: (from: Date | null) => void;
    onDateToChange?: (to: Date | null) => void;
}

const CustomRangePicker = (props: CustomRangePickerProps) => {
    const { valueFrom, valueTo, onDateRangeChange,onDateFromChange,onDateToChange } = props;
    const [t] = useTranslation();    
    const textFieldRef = useRef<HTMLDivElement | null>(null);
    const rangeCompleteRef = useRef(false);
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const [startDate, setStartDate] = useState<Dayjs | null>(null);
    const [endDate, setEndDate] = useState<Dayjs | null>(null);
    const today = useMemo(() => dayjs().endOf("day"), [] );
    const maxSelectableDate = useMemo(() => dayjs().subtract(1, "day").endOf("day"), [] );
    const currentMonth = useMemo(() => today.startOf("month"), [today] );
    const [leftMonth, setLeftMonth] = useState<Dayjs>(currentMonth.subtract(1, "month"));
    // rightMonth is always exactly one month after leftMonth — no separate state needed
    const rightMonth = leftMonth.add(1, "month");
    const [leftView, setLeftView] = useState<"day" | "month" | "year">("day");
    const [rightView, setRightView] = useState<"day" | "month" | "year">("day");
    const [calendarValidationError, setCalendarValidationError] = useState<string>("");
    const open = Boolean(anchorEl);
    const prevDisabled = !leftMonth.isAfter(calenderStartDate, "month");
    const nextDisabled = !rightMonth.isBefore(currentMonth, "month");
    const hiddenArrowSx = { visibility: "hidden" as const };

    /**
     * @description - Clamps the desired leftMonth so that rightMonth (= left+1) never
     * exceeds currentMonth and never goes before calenderStartDate.
     * This is the ONLY function that should call setLeftMonth — it guarantees
     * the two calendars are always adjacent.
     */
    const setLeftSafe = useCallback((month: Dayjs) => {
        const m = month.startOf("month");
        const maxLeft = currentMonth.subtract(1, "month"); // rightMonth = maxLeft+1 = currentMonth
        if (m.isBefore(calenderStartDate, "month")) {
            setLeftMonth(calenderStartDate.startOf("month"));
        } else if (m.isAfter(maxLeft, "month")) {
            setLeftMonth(maxLeft);
        } else {
            setLeftMonth(m);
        }
    }, [currentMonth]);

    const handlePrev = () => setLeftSafe(leftMonth.subtract(1, "month"));
    const handleNext = () => setLeftSafe(leftMonth.add(1, "month"));

    const notifyDateRangeChange = useCallback((from: Date | null, to: Date | null) => {
        onDateFromChange?.(from);
        onDateToChange?.(to);
        onDateRangeChange?.(from, to);
    }, [onDateFromChange, onDateRangeChange, onDateToChange]);

    const closeCompletedRange = () => {
        rangeCompleteRef.current = true;
        setAnchorEl(null);
    };

    /**
     * @description - Opens the calendar popover and sets the text field as anchor element.
     */
    const handleOpenCalendar = () => {
        if (textFieldRef.current) {
            rangeCompleteRef.current = false;
            setLeftView("day");
            setRightView("day");
            if (startDate) {
                setLeftSafe(startDate.startOf("month"));
            }
            setAnchorEl(textFieldRef.current);
        }
    };

    /**
     * @description - Closes the calendar popover only after a complete range is selected.
     */
    const handleCloseCalendar = () => {
        if (rangeCompleteRef.current || (startDate && endDate)) {
            setAnchorEl(null);
            return;
        }

        onDateFromChange?.(startDate ? startDate.toDate() : null);
        onDateToChange?.(endDate ? endDate.toDate() : null);
    };

    const getAllowedRangeDays = useCallback((firstDate: Dayjs, secondDate: Dayjs) => {
        const start = firstDate.isBefore(secondDate, "day") ? firstDate : secondDate;
        const end = firstDate.isBefore(secondDate, "day") ? secondDate : firstDate;
        for (let year = start.year(); year <= end.year(); year++) {
            const isLeap = (year % 4 === 0 && year % 100 !== 0) || (year % 400 === 0);
            if (!isLeap) continue;
            const leapDay = dayjs(`${year}-02-29`);
            if (!leapDay.isBefore(start, "day") && !leapDay.isAfter(end, "day")) {
                return 366;
            }
        }
        return DEFAULT_MAX_RANGE_DAYS;
    }, []);

    /**
     * @description - Position calendars so the given date's month is on the LEFT.
     * Right calendar will automatically show the next month.
     */
    const showSingleMonthLeft = useCallback((date: Dayjs) => {
        setLeftSafe(date.startOf("month"));
    }, [setLeftSafe]);

    /**
     * @description - Position calendars so the given date's month is on the RIGHT.
     * Left calendar will automatically show the previous month.
     */
    const showSingleMonthRight = useCallback((date: Dayjs) => {
        setLeftSafe(date.startOf("month").subtract(1, "month"));
    }, [setLeftSafe]);

    /**
     * @description - Convenience alias — positions left calendar on the given date's month.
     */
    const showSingleMonth = useCallback((date: Dayjs) => {
        setLeftSafe(date.startOf("month"));
    }, [setLeftSafe]);

    /**
     * @description - Updates visible calendar months based on selected start and end dates.
     * Always positions so endMonth appears on the right (left = endMonth - 1),
     * keeping the calendars adjacent.
     * @param start - Range start date
     * @param end - Range end date
     */
    const showRangeMonths = useCallback((start: Dayjs, end: Dayjs) => {
        const endMonth = end.startOf("month");
        setLeftSafe(endMonth.subtract(1, "month"));
    }, [setLeftSafe]);

    /**
     * @description - Handles month navigation for the left calendar.
     * Moving left calendar also moves right calendar (always adjacent).
     */
    const handleLeftMonthChange = (month: Dayjs) => {
        setLeftSafe(month.startOf("month"));
    };

    /**
     * @description - Handles month navigation for the right calendar.
     * Moving right calendar also moves left calendar (always adjacent).
     */
    const handleRightMonthChange = (month: Dayjs) => {
        setLeftSafe(month.startOf("month").subtract(1, "month"));
    };

    useEffect(() => {
        const nextStartDate = valueFrom ? dayjs(valueFrom) : null;
        const nextEndDate = valueTo ? dayjs(valueTo) : null;
        setStartDate(nextStartDate);
        setEndDate(nextEndDate);
        if (anchorEl) return;
        if (nextStartDate && nextEndDate) {
            showRangeMonths(nextStartDate, nextEndDate);
            setCalendarValidationError("");
            return;
        }
        if (nextStartDate) {
            showSingleMonth(nextStartDate);
        }
    }, [valueFrom, valueTo]);

    /**
     * @description - Checks whether the provided date falls within the selected date range.
     * @param date - Date to validate
     * @returns True when date is inside range
     */
    const isInRange = (date: Dayjs): boolean => {
        if (!startDate || !endDate) return false;
        return (
            date.isAfter(startDate, "day") &&
            date.isBefore(endDate, "day")
        );
    };

    /**
     * @description - Checks whether the provided date matches the start or end date.
     * @param date - Date to validate
     * @returns True when date is a boundary date
      */
    const isStartOrEndDate = (date: Dayjs): boolean => {
        if (startDate && date.isSame(startDate, "day"))
            return true;
        if (endDate && date.isSame(endDate, "day"))
            return true;
        return false;
    };

    /**
     * @description - Handles date selection and range creation logic.
     * Supports start date selection, end date selection,
     * range reset and maximum range validation.
     * @param date - Selected date
     */
    const handleDateSelect = (date: Dayjs | null, side: "left" | "right") => {
        if (!date) return;
        if (rangeCompleteRef.current) return;
        if (date.isBefore(calenderStartDate, "day")) return;
        if (date.isAfter(maxSelectableDate, "day")) return;
        if (side === "left" && leftView !== "day") {
            if (leftView === "year") {
                setLeftMonth(date.startOf("year"));
                setLeftView("month");
            } else if (leftView === "month") {
                showSingleMonthLeft(date);
                setLeftView("day");
                setRightView("day");
            }
            return;
        }
        if (side === "right" && rightView !== "day") {
            if (rightView === "year") {
                setLeftSafe(date.startOf("year").subtract(1, "month"));
                setRightView("month");
            } else if (rightView === "month") {
                showSingleMonthRight(date);
                setRightView("day");
                setLeftView("day");
            }
            return;
        }
        if (startDate && endDate) {
            setStartDate(date);
            setEndDate(null);
            if (side === "left") showSingleMonthLeft(date);
            else showSingleMonthRight(date);
            notifyDateRangeChange?.(date.toDate(), null);
            return;
        }
        if (!startDate) {
            setStartDate(date);
            if (side === "left") showSingleMonthLeft(date);
            else showSingleMonthRight(date);
            notifyDateRangeChange?.(date.toDate(), null);
            return;
        }
        if (startDate) {
            if (date.isBefore(startDate, "day")) {
                const previousStart = startDate;
                const allowedStartDate = getAllowedRangeDays(date, previousStart);
                if (Math.abs(date.diff(previousStart, "day")) >= allowedStartDate) {
                    setCalendarValidationError(String(t("business_report.maximum_valid_range")));
                    return;
                }
                setStartDate(date);
                setEndDate(previousStart);
                notifyDateRangeChange?.(date.toDate(), previousStart.toDate());
                closeCompletedRange();
                return;
            }
            const allowedEndDate = getAllowedRangeDays(startDate, date);
            if (date.diff(startDate, "day") >= allowedEndDate) {
                setCalendarValidationError(String(t("business_report.maximum_valid_range")));
                return;
            }
            setEndDate(date);
            notifyDateRangeChange?.(startDate.toDate(), date.toDate());
            closeCompletedRange();
        }
    };

    /**
     * @description - Renders custom calendar day styles for selected dates
     * and dates that fall within the selected range.
     * @param date - Calendar date
     * @param _selectedDays - Selected dates
     * @param pickersDayProps - Day component properties
     * @returns Custom PickersDay component
     */
    const renderRangeDay = (date: Dayjs, _selectedDays: Dayjs[],pickersDayProps: PickersDayProps<Dayjs>) => {
        const isBoundaryDate = isStartOrEndDate(date);
        const isBetweenDates = isInRange(date);
        return (
            <PickersDay
                {...pickersDayProps}
                disableMargin={false}
                selected={isBoundaryDate}
                sx={{
                    ...(isBetweenDates && {
                        borderRadius: 0,
                        backgroundColor: "#c0e3d9",
                        color: "black",
                        "&:hover, &:focus": {
                            backgroundColor: "#c0e3d9",
                        },
                    }),
                    ...(isBoundaryDate && {
                        borderRadius: 0,
                        backgroundColor: "#00684d !important",
                        color: "black",
                        "&:hover, &:focus": {
                            backgroundColor: "#00684d !important",
                        },
                    }),
                }}
            />
        );
    };

    const displayValue = () => {
        if (startDate && endDate) {
            return `${startDate.format("YYYY/MM/DD")} - ${endDate.format("YYYY/MM/DD")}`;
        }
        if (startDate) {
            return `${startDate.format("YYYY/MM/DD")} - `;
        }
        return "";
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="ja" dateFormats={{ monthAndYear: "YYYY年MM月" }}>
            <TextField
                ref={textFieldRef}
                fullWidth
                label={t("business_report.date_range")}
                value={displayValue()}
                onClick={handleOpenCalendar}
                InputProps={{
                    readOnly: true,
                    endAdornment: (
                        <InputAdornment position="end">
                            <IconButton
                                aria-label="open date range calendar"
                                edge="end"
                                size="small"
                                onClick={(event) => {
                                    event.stopPropagation();
                                    handleOpenCalendar();
                                }}
                                sx={{ mr: -0.5 }}
                            >
                                <EventIcon sx={{ fontSize: 26 }} />
                            </IconButton>
                        </InputAdornment>
                    ),
                }}
            />

            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={handleCloseCalendar}
                anchorOrigin={{vertical: "bottom",horizontal: "left",}}
            >
                <Box p={2} gap={2}>
                    <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                        px={1}
                        mb={-3}
                        sx={{ position: "relative", zIndex: 1 }}
                    >
                        <IconButton
                            size="small"
                            onClick={handlePrev}
                            disabled={prevDisabled}
                            aria-label="previous month"
                        >
                            <ChevronLeftIcon />
                        </IconButton>
                        <IconButton
                            size="small"
                            onClick={handleNext}
                            disabled={nextDisabled}
                            aria-label="next month"
                        >
                            <ChevronRightIcon />
                        </IconButton>
                    </Box>

                    {/* Calendar pair */}
                    <Box display="flex">
                        {/* LEFT MONTH */}
                        <Box sx={{alignItems:"center"}}>
                            {leftView === "month" ? (
                                <Box sx={{ p: 2 }}>
                                    {/* Year header with back arrow */}
                                    <Box display="flex" alignItems="center" justifyContent="center" mb={1}>
                                        <IconButton
                                            size="small"
                                            onClick={() => setLeftView("year")}
                                            aria-label={String(t("business_report.back_to_year"))}
                                        >
                                            <ChevronLeftIcon />
                                        </IconButton>
                                        <Typography
                                            variant="subtitle1"
                                            fontWeight="bold"
                                            sx={{ mx: 1, minWidth: 60, textAlign: "center" }}
                                        >
                                            {leftMonth.format("YYYY年")}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 2 }}>
                                        {Array.from({ length: 12 }).map((_, i) => {
                                            const monthDate = leftMonth.startOf("year").add(i, "month");
                                            const disabled = monthDate.isBefore(calenderStartDate, "month") || monthDate.isAfter(maxSelectableDate, "month");
                                            return (
                                                <Box
                                                    key={i}
                                                    onClick={() => { if (!disabled) { showSingleMonthLeft(monthDate); setLeftView("day"); setRightView("day"); } }}
                                                    sx={{
                                                        py: 1.5,
                                                        px: 2,
                                                        textAlign: "center",
                                                        borderRadius: 2,
                                                        cursor: disabled ? "default" : "pointer",
                                                        backgroundColor: disabled ? "transparent" : "transparent",
                                                        color: disabled ? "#aaa" : "inherit",
                                                    }}
                                                >
                                                    {monthDate.format("M月")}
                                                </Box>
                                            );
                                        })}
                                    </Box>
                                </Box>
                            ) : (
                                <Box className="custom-date-range-picker-calendar">
                                    <CalendarPicker
                                        key={`left-${leftMonth.format("YYYY-MM")}`}
                                        view={leftView}
                                        date={leftMonth}
                                        defaultCalendarMonth={leftMonth}
                                        onChange={(d) => handleDateSelect(d, "left")}
                                        onViewChange={(v) => setLeftView(v)}
                                        onMonthChange={handleLeftMonthChange}
                                        minDate={calenderStartDate}
                                        maxDate={maxSelectableDate}
                                        componentsProps={{
                                            leftArrowButton: { sx: hiddenArrowSx },
                                            rightArrowButton: { sx: hiddenArrowSx },
                                        }}
                                        renderDay={renderRangeDay}
                                        disableFuture
                                    />
                                </Box>
                            )}
                        </Box>

                        {/* RIGHT MONTH */}
                        <Box>
                            {rightView === "month" ? (
                                <Box sx={{ p: 2 }}>
                                    {/* Year header with back arrow */}
                                    <Box display="flex" alignItems="center" justifyContent="center" mb={1}>
                                        <IconButton
                                            size="small"
                                            onClick={() => setRightView("year")}
                                            aria-label={String(t("business_report.back_to_year"))}
                                        >
                                            <ChevronLeftIcon />
                                        </IconButton>
                                        <Typography
                                            variant="subtitle1"
                                            fontWeight="bold"
                                            sx={{ mx: 1, minWidth: 60, textAlign: "center" }}
                                        >
                                            {rightMonth.format("YYYY年")}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 2 }}>
                                        {Array.from({ length: 12 }).map((_, i) => {
                                            const monthDate = rightMonth.startOf("year").add(i, "month");
                                            const disabled = monthDate.isBefore(calenderStartDate, "month") || monthDate.isAfter(maxSelectableDate, "month");
                                            return (
                                                <Box
                                                    key={i}
                                                    onClick={() => { if (!disabled) { showSingleMonthRight(monthDate); setRightView("day"); setLeftView("day"); } }}
                                                    sx={{
                                                        py: 1.5,
                                                        px: 2,
                                                        textAlign: "center",
                                                        borderRadius: 2,
                                                        cursor: disabled ? "default" : "pointer",
                                                        backgroundColor: disabled ? "transparent" : "transparent",
                                                        color: disabled ? "#aaa" : "inherit",
                                                    }}
                                                >
                                                    {monthDate.format("M月")}
                                                </Box>
                                            );
                                        })}
                                    </Box>
                                </Box>
                            ) : (
                                <Box className="custom-date-range-picker-calendar">
                                    <CalendarPicker
                                        key={`right-${rightMonth.format("YYYY-MM")}`}
                                        view={rightView}
                                        date={rightMonth}
                                        defaultCalendarMonth={rightMonth}
                                        onChange={(d) => handleDateSelect(d, "right")}
                                        onViewChange={(v) => setRightView(v)}
                                        onMonthChange={handleRightMonthChange}
                                        minDate={calenderStartDate}
                                        maxDate={maxSelectableDate}
                                        componentsProps={{
                                            leftArrowButton: { sx: hiddenArrowSx },
                                            rightArrowButton: { sx: hiddenArrowSx },
                                        }}
                                        renderDay={renderRangeDay}
                                        disableFuture
                                    />
                                </Box>
                            )}
                        </Box>
                    </Box>
                    {calendarValidationError &&
                        <Box display="flex" justifyContent="space-between" alignItems="center" mt={1} bgcolor="#fdecea" p={1} borderRadius={1}>
                            <Typography color="red">{calendarValidationError}</Typography>
                            <CloseIcon onClick={() => setCalendarValidationError("")} className="cursor-pointer"/>
                        </Box>
                    }
                </Box>
            </Popover>
        </LocalizationProvider>
    );
};
export default CustomRangePicker;
 